/**
 * Sort-related constants for the mobile scouting targets list.
 *
 * Centralises sort options, field-to-key mappings, score field sets,
 * and badge labels used by MobileTargetsListContainer.
 * @module features/scouting/constants/mobileSortConstants
 */

import { SortField } from "@/generated/graphql";

import type { ScoutingTargetRow } from "../types/scouting-target";

/**
 * Scouting All-Targets mobile sort options (SE-4146).
 *
 * The four fields shown in the redesigned sort sheet — kept deliberately
 * separate from the shared {@link MOBILE_SORT_OPTIONS} (which the /search tab
 * consumes) so trimming the scouting list to match the Figma design does not
 * change the search surface. Labels here are the long, sheet-facing strings
 * ("Highest GPR"); short button/toast labels live in
 * {@link SCOUTING_MOBILE_SORT_FIELD_LABELS}.
 */
export const SCOUTING_MOBILE_SORT_OPTIONS = [
  { field: "GPR", label: "Highest GPR" },
  { field: "PLAYER_NAME", label: "Alphabetically Player Name" },
  { field: "SCOUT_RATING", label: "Highest Scout Ranking" },
  { field: "PIPELINE_STAGE", label: "Pipeline Stage" },
] as const;

/**
 * Short labels for the scouting sort fields, used by the toolbar Sort button
 * ("Sort: GPR") and the "Sorted by …" toast. Distinct from the sheet labels so
 * the compact button stays readable inside its `max-w-1/3` clamp.
 */
export const SCOUTING_MOBILE_SORT_FIELD_LABELS: Readonly<
  Record<string, string>
> = {
  GPR: "GPR",
  PLAYER_NAME: "Player Name",
  SCOUT_RATING: "Scout Ranking",
  PIPELINE_STAGE: "Pipeline Stage",
};

/**
 * Scouting sort fields that must be sorted server-side via the
 * `ScoutingListAllTargets` query's `sortBy` argument.
 *
 * SE-4146: SCOUT_RATING and PIPELINE_STAGE have no per-row value on
 * `ScoutingTargetRow`, so they cannot be sorted client-side by
 * {@link sortTargetRows}; they must round-trip to the backend. GPR is included
 * so it is sorted once (server-side) rather than being re-sorted on the loaded
 * page. PLAYER_NAME is intentionally absent — no backend `SortField` exists for
 * it, so it stays client-side.
 */
export const SCOUTING_SERVER_SORT_FIELDS: Readonly<Record<string, SortField>> =
  {
    GPR: SortField.Gpr,
    SCOUT_RATING: SortField.ScoutRating,
    PIPELINE_STAGE: SortField.PipelineStage,
  };

/** Mobile sort options — real sortable fields with mobile-friendly labels */
export const MOBILE_SORT_OPTIONS = [
  { field: "GPR", label: "GPR" },
  { field: "PLAYER_NAME", label: "Alphabetically Player Name" },
  { field: "FIT_SCORE", label: "Player Style Fit" },
  { field: "TEAM_STYLE_FIT", label: "Team Style Fit" },
  { field: "CARRYING", label: "Carrying" },
  { field: "DRIBBLING", label: "Dribbling" },
  { field: "PASSING", label: "Passing" },
  { field: "SHOOTING", label: "Shooting" },
  { field: "MONTHS_REMAINING", label: "Months Remaining" },
] as const;

/** Maps sort field to display label for toast notification */
export const MOBILE_SORT_FIELD_LABELS: Readonly<Record<string, string>> =
  MOBILE_SORT_OPTIONS.reduce(
    (acc, opt) => ({ ...acc, [opt.field]: opt.label }),
    {} as Record<string, string>
  );

/** Maps sort field enum values to ScoutingTargetRow property keys */
export const SORT_FIELD_TO_KEY: Readonly<
  Record<string, keyof ScoutingTargetRow>
> = {
  GPR: "gpr",
  PLAYER_NAME: "playerName",
  FIT_SCORE: "playerStyleFit",
  TEAM_STYLE_FIT: "teamStyleFit",
  CARRYING: "carrying",
  DRIBBLING: "dribbling",
  PASSING: "passing",
  SHOOTING: "shooting",
  GK_PASSING: "gkPassing",
  GK_POSITIONING: "gkPositioning",
  GK_SHOT_STOPPING: "gkShotStopping",
  MONTHS_REMAINING: "monthsOnContract",
} as const;

/** Score-type sort fields that should display their value in the card badge */
export const SCORE_SORT_FIELDS = new Set([
  "GPR",
  "FIT_SCORE",
  "TEAM_STYLE_FIT",
  "CARRYING",
  "DRIBBLING",
  "PASSING",
  "SHOOTING",
  "GK_PASSING",
  "GK_POSITIONING",
  "GK_SHOT_STOPPING",
  "MONTHS_REMAINING",
]);

/** Full-text labels for score badge prefix — GPR excluded (default, no label needed) */
export const SCORE_BADGE_LABELS: Readonly<Record<string, string>> = {
  FIT_SCORE: "Player Style Fit",
  TEAM_STYLE_FIT: "Team Style Fit",
  CARRYING: "Carrying",
  DRIBBLING: "Dribbling",
  PASSING: "Passing",
  SHOOTING: "Shooting",
  GK_PASSING: "GK Passing",
  GK_POSITIONING: "GK Positioning",
  GK_SHOT_STOPPING: "GK Shot Stopping",
  MONTHS_REMAINING: "Months Remaining",
};
